import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
} from 'formik'
import {
  Paper,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
  TextField,
  Link as MuiLink,
} from '@material-ui/core'
import { PasswordPolicy } from 'password-sheriff'
import { good as Policy } from 'auth0-password-policies'

import * as UserActionCreators from '../../actions/user'
import CONFIG from '../../config'
import axios from '../../utils/api'
import { login } from '../../utils/AuthService'

import SpinnerSubmitButton from '../controls/buttons/SpinnerSubmitButton'
import InputValidationTooltip from '../controls/input/InputValidationTooltip'
import PasswordStrength from '../intro/PasswordStrength'

import SmallArrowRight from '../../../assets/images/icons/ico_arrow_small.svg'

class ResetPassword extends React.Component {
  static propTypes = {
    /* HOC/middleware props */
    classes: PropTypes.object.isRequired,
    /* mapped props */
    users: PropTypes.object.isRequired,
    userActions: PropTypes.object.isRequired,
  }

  static mapStateToProps(state) {
    return {
      users: state.users,
    }
  }

  static mapDispatchToProps(dispatch) {
    return {
      userActions: bindActionCreators(UserActionCreators, dispatch),
    }
  }

  static styles = (theme) => ({
    promptItem: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      [theme.breakpoints.up('md')]: {
        width: 'auto',
      },
    },
    promptPaper: {
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: '520px',
        padding: theme.spacing(5),
      },
    },
    form: {
      marginTop: theme.spacing(3),
    },
    formControl: {
      width: '100%',
      marginBottom: theme.spacing(2),
      '& [class^="PasswordStrength"] ul:first-child': {
        paddingTop: '70px',
      },
    },
    labelRoot: theme.formControlLabelRoot,
    label: theme.label,
    textFieldRoot: theme.loginTextFieldRoot,
    error: {
      color: 'red',
    },
    actionItem: {
      marginTop: theme.spacing(4),
    },
    link: theme.loginLink,
  })

  componentDidMount() {
    this.props.userActions.currentUser()
  }

  validateForm = (values) => {
    const errors = {}

    const policy = new PasswordPolicy(Policy)
    if (!policy.check(values.password)) {
      errors.password = 'Password does not meet password policy'
    }

    if (!values.confirm) {
      errors.confirm = 'Password confirmation required'
    } else if (values.confirm !== values.password) {
      errors.confirm = 'Password confirmation must match'
    }

    return errors
  }

  handleSave = (values, { setErrors, setSubmitting }) => {
    const { email } = this.props.users.me

    const user = { password: values.password }

    axios
      .put(`${CONFIG.apiBase}/users/me`, user)
      .then(
        () => {
          login(email, values.password, () => {
            setErrors({ server: 'An error has occurred. Please contact your BlokSense Administrator.' })
          })
        },
        () => {
          setSubmitting(false)
          setErrors({
            server: 'An error has occurred. Please contact your BlokSense Administrator.',
          })
        },
      )
  }

  render() {
    const { classes } = this.props

    return (
      <Grid item className={classes.promptItem}>
        <Paper elevation={3} square classes={{ root: classes.promptPaper }}>
          <Typography variant="h1">Reset your password</Typography>
          <Typography variant="h2">Strong passwords include numbers, letters, and punctuation marks.</Typography>

          <Formik
            initialValues={{
              password: '',
              confirm: '',
            }}
            validate={this.validateForm}
            onSubmit={this.handleSave}
          >
            {(formikProps) => (
              <Form className={classes.form}>
                <Field
                  name="password"
                  value={formikProps.values.password}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                >
                  {({ field, form: { touched, errors } }) => {
                    const showError = touched.password && Boolean(errors.password)
                    return (
                      <FormControl className={classes.formControl}>
                        <FormControlLabel
                          classes={{ root: classes.labelRoot, label: classes.label }}
                          label="New Password"
                          labelPlacement="top"
                          control={(
                            <TextField
                              {...field}
                              type="password"
                              classes={{ root: classes.textFieldRoot }}
                              error={showError}
                              fullWidth
                              variant="outlined"
                            />
                          )}
                        />
                        <ErrorMessage name="password">
                          { (msg) => <InputValidationTooltip title={msg} /> }
                        </ErrorMessage>
                        { showError && <PasswordStrength password={formikProps.values.password} policy={Policy} /> }
                      </FormControl>
                    )
                  }}
                </Field>

                <Field
                  name="confirm"
                  value={formikProps.values.confirm}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                >
                  {({ field, form: { touched, errors } }) => (
                    <FormControl className={classes.formControl}>
                      <FormControlLabel
                        classes={{ root: classes.labelRoot, label: classes.label }}
                        label="Confirm Password"
                        labelPlacement="top"
                        control={(
                          <TextField
                            {...field}
                            type="password"
                            classes={{ root: classes.textFieldRoot }}
                            error={touched.confirm && Boolean(errors.confirm)}
                            fullWidth
                            variant="outlined"
                          />
                        )}
                      />
                      <ErrorMessage name="confirm">
                        { (msg) => <InputValidationTooltip title={msg} /> }
                      </ErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Grid container justify="space-between" alignItems="center">
                  <Grid item className={classes.actionItem}>
                    <SpinnerSubmitButton
                      onClick={formikProps.handleSubmit}
                      isSubmitting={formikProps.isSubmitting}
                    >
                      Reset password
                      <img src={SmallArrowRight} alt="→" />
                    </SpinnerSubmitButton>
                  </Grid>
                  <Grid item className={classes.actionItem}>
                    <MuiLink href="/login" className={classes.link}>
                      Return to sign in
                    </MuiLink>
                  </Grid>
                </Grid>

                { formikProps.errors.server
                  && <div className={classes.error}>{formikProps.errors.server}</div>}
              </Form>
            )}
          </Formik>
        </Paper>
      </Grid>
    )
  }
}

export default withStyles(ResetPassword.styles)(connect(
  ResetPassword.mapStateToProps,
  ResetPassword.mapDispatchToProps,
)(ResetPassword))
